<div class="ks-container">
  <header class="ks-page-header ks-rise-1">
    <div>
      <h1 class="ks-page-title">Tipos de campo</h1>
      <p class="ks-page-subtitle">
        Las piezas con las que se arman las plantillas. Cada tipo define cómo captura la app ese
        dato: en claro o cifrado, en una línea o en varias.
      </p>
    </div>

    <button type="button" class="ks-btn ks-btn--primary" (click)="create()">
      <i class="bi bi-plus-lg" aria-hidden="true"></i>
      Nuevo tipo de campo
    </button>
  </header>

  <div class="ks-filter-bar ks-rise-2">
    <div class="ks-filter-bar__controls">
      <label class="ks-visually-hidden" for="ks-field-types-search">Buscar tipo de campo</label>
      <input
        id="ks-field-types-search"
        type="search"
        class="ks-field"
        placeholder="Buscar por identificador o descripción"
        [value]="search()"
        (input)="onSearchChange($any($event.target).value)" />

      <!--
        ng-select, no <select>: todos los desplegables de la consola deben
        permitir BUSCAR. El id del input interno lo pone `labelForId`, asi que el
        `<label for>` de arriba sigue apuntando al elemento que recibe el foco.
        "Todos los estados" queda como item REAL de la lista (no como
        placeholder) para no cambiar el comportamiento del select nativo que
        reemplaza.
      -->
      <div class="ks-field-types__status-filter">
        <label class="ks-visually-hidden" for="ks-field-types-status">Filtrar por estado</label>
        <ng-select
          labelForId="ks-field-types-status"
          [items]="statusOptions"
          bindLabel="label"
          bindValue="value"
          [ngModel]="statusFilter()"
          (change)="onStatusFilterChange($event)"
          placeholder="Todos los estados"
          notFoundText="Sin resultados"
          clearAllText="Quitar selección"
          loadingText="Cargando…"
          typeToSearchText="Escribe para buscar"
          [searchable]="true"
          [clearable]="true" />
      </div>
    </div>

    <div class="ks-filter-bar__actions">
      <span class="ks-count" aria-live="polite">
        {{ visibleFieldTypes().length }} de {{ fieldTypes().length }} · {{ activeCount() }} activos
      </span>
    </div>
  </div>

  <section class="ks-panel ks-rise-3" aria-labelledby="ks-field-types-heading">
    <div class="ks-card__header">
      <h2 class="ks-card__title" id="ks-field-types-heading">Catálogo</h2>
    </div>

    <!-- Orden obligatorio: carga -> error -> vacio -> listado. El estado vacio
         solo puede aparecer cuando ya se sabe con certeza que no hay datos.
         El esqueleto debe corresponder a la vista que se va a mostrar (filas
         o tarjetas): si no, hay salto de layout al terminar de cargar. -->
    @if (!loaded() || loading()) {
      @if (!isMobile()) {
        <app-skeleton shape="line" height="46px" [count]="6" label="Cargando tipos de campo" />
      } @else {
        <ul class="ks-field-types__cards">
          @for (item of skeletonCards; track item) {
            <li class="ks-field-types__card">
              <div class="ks-skeleton-stack">
                <app-skeleton shape="line" width="40%" height="16px" label="Cargando tipos de campo" />
                <app-skeleton shape="line" width="75%" height="12px" />
                <app-skeleton shape="line" width="55%" height="12px" />
              </div>
            </li>
          }
        </ul>
      }
    } @else if (error()) {
      <app-empty-state
        icon="wifi-off"
        title="No se pudieron cargar los tipos de campo"
        [message]="error()!">
        <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="load()">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Reintentar
        </button>
      </app-empty-state>
    } @else if (visibleFieldTypes().length === 0) {
      <app-empty-state
        icon="ui-checks-grid"
        title="Sin tipos de campo"
        message="No hay tipos que coincidan con el filtro. Crea uno o cambia la búsqueda.">
        <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="create()">
          <i class="bi bi-plus-lg" aria-hidden="true"></i>
          Nuevo tipo de campo
        </button>
      </app-empty-state>
    } @else if (!isMobile()) {
      <div class="ks-table-wrap">
        <table class="ks-table ks-table--sticky-first">
          <caption class="ks-visually-hidden">
            Tipos de campo del catálogo, ordenados por identificador.
          </caption>

          <thead>
            <tr>
              <th scope="col">Identificador</th>
              <th scope="col">Descripción</th>
              <th scope="col">Comportamiento</th>
              <th scope="col">Estado</th>
              <th scope="col">Actualizado</th>
              <th scope="col"><span class="ks-visually-hidden">Acciones</span></th>
            </tr>
          </thead>

          <tbody>
            @for (fieldType of visibleFieldTypes(); track fieldType.fieldType) {
              <tr>
                <td>
                  <span class="ks-chip ks-chip--purple ks-mono">{{ fieldType.fieldType }}</span>
                </td>

                <td>{{ fieldType.description }}</td>

                <td>
                  <div class="ks-flags">
                    @if (fieldType.protected) {
                      <span class="ks-badge ks-badge--warning" title="El valor se cifra y se oculta">
                        <i class="bi bi-shield-lock" aria-hidden="true"></i>
                        Protegido
                      </span>
                    }
                    @if (fieldType.multiline) {
                      <span class="ks-badge ks-badge--info" title="Se captura en varias líneas">
                        <i class="bi bi-textarea-resize" aria-hidden="true"></i>
                        Multilínea
                      </span>
                    }
                    @if (!fieldType.protected && !fieldType.multiline) {
                      <span class="ks-badge ks-badge--muted">Texto simple</span>
                    }
                  </div>
                </td>

                <td>
                  @if (fieldType.status) {
                    <span class="ks-badge ks-badge--success">Activo</span>
                  } @else {
                    <span class="ks-badge ks-badge--muted">Inactivo</span>
                  }
                </td>

                <td class="ks-muted">{{ fieldType.updatedAt | dateToMoment }}</td>

                <td>
                  <div class="ks-table__actions">
                    <button
                      type="button"
                      class="ks-icon-btn"
                      [attr.aria-label]="'Editar el tipo de campo ' + fieldType.fieldType"
                      (click)="edit(fieldType)">
                      <i class="bi bi-pencil" aria-hidden="true"></i>
                    </button>

                    @if (fieldType.status) {
                      <button
                        type="button"
                        class="ks-icon-btn ks-icon-btn--danger"
                        [attr.aria-label]="'Desactivar el tipo de campo ' + fieldType.fieldType"
                        (click)="deactivate(fieldType)">
                        <i class="bi bi-slash-circle" aria-hidden="true"></i>
                      </button>
                    } @else {
                      <button
                        type="button"
                        class="ks-icon-btn"
                        [attr.aria-label]="'Activar el tipo de campo ' + fieldType.fieldType"
                        (click)="activate(fieldType)">
                        <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <!-- Sin página de detalle propia (se edita en modal): la tarjeta NO
           lleva `appRowLink` ni cursor de puntero — sería insinuar una
           navegación que no existe. Las acciones son las mismas de la tabla. -->
      <ul class="ks-field-types__cards">
        @for (fieldType of visibleFieldTypes(); track fieldType.fieldType) {
          <li class="ks-field-types__card" [class.ks-field-types__card--off]="!fieldType.status">
            <div class="ks-field-types__card-head">
              <div class="ks-field-types__identity-body">
                <p class="ks-field-types__card-description">{{ fieldType.description }}</p>
                <span class="ks-chip ks-chip--purple ks-mono ks-field-types__code">{{ fieldType.fieldType }}</span>
              </div>

              @if (fieldType.status) {
                <span class="ks-badge ks-badge--success">Activo</span>
              } @else {
                <span class="ks-badge ks-badge--muted">Inactivo</span>
              }
            </div>

            <div class="ks-field-types__card-meta">
              <div class="ks-flags">
                @if (fieldType.protected) {
                  <span class="ks-badge ks-badge--warning" title="El valor se cifra y se oculta">
                    <i class="bi bi-shield-lock" aria-hidden="true"></i>
                    Protegido
                  </span>
                }
                @if (fieldType.multiline) {
                  <span class="ks-badge ks-badge--info" title="Se captura en varias líneas">
                    <i class="bi bi-textarea-resize" aria-hidden="true"></i>
                    Multilínea
                  </span>
                }
                @if (!fieldType.protected && !fieldType.multiline) {
                  <span class="ks-badge ks-badge--muted">Texto simple</span>
                }
              </div>

              <span class="ks-field-types__card-updated ks-muted">
                Actualizado el {{ fieldType.updatedAt | dateToMoment: 'DD/MM/YYYY' }}
              </span>
            </div>

            <div class="ks-table__actions">
              <button
                type="button"
                class="ks-icon-btn"
                [attr.aria-label]="'Editar el tipo de campo ' + fieldType.fieldType"
                (click)="edit(fieldType)">
                <i class="bi bi-pencil" aria-hidden="true"></i>
              </button>

              @if (fieldType.status) {
                <button
                  type="button"
                  class="ks-icon-btn ks-icon-btn--danger"
                  [attr.aria-label]="'Desactivar el tipo de campo ' + fieldType.fieldType"
                  (click)="deactivate(fieldType)">
                  <i class="bi bi-slash-circle" aria-hidden="true"></i>
                </button>
              } @else {
                <button
                  type="button"
                  class="ks-icon-btn"
                  [attr.aria-label]="'Activar el tipo de campo ' + fieldType.fieldType"
                  (click)="activate(fieldType)">
                  <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                </button>
              }
            </div>
          </li>
        }
      </ul>
    }
  </section>
</div>
